import { Link } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid-premium';
import { useCallback, useMemo } from 'react';
import { useProductsTFunction } from '@retail/products/i18n';
import {
  AddButton,
  ColumnHeaderWithHelpText,
  CopyToClipboard,
  NoTableRowsOverlay,
  PaginatedDataGrid,
  StackedTextCell,
} from '@retail/components';
import { Competitor } from '@retail/retailer/types';
import { dataGridBoldClass } from '@shared/styles';
import { MonitoringAssortmentItem } from '@retail/monitoring-assortment/types';
import { CompetitorPriceCell, NoPrice, OurPriceCell } from '../../ProductCell';
import RetailerColumnHeader from '../../ProductCell/RetailerColumnHeader';
import { useRowColorGrouping } from '@retail/hooks';
import { Pagination } from '@shared/hooks';

interface Props {
  items: MonitoringAssortmentItem[];
  totalItems: number | undefined;
  pagination: Pagination;
  includeVat: boolean;
  competitors: Competitor[];
  openAddItemsDialog: () => void;
  setItemToView: (mgItemNumber: number) => void;
  loading: boolean;
}

type GridColMonitoringAssortmentItem = GridColDef<MonitoringAssortmentItem>;

const baseColumnProps: Partial<GridColMonitoringAssortmentItem> = {
  width: 170,
  sortable: false,
  align: 'left',
  headerAlign: 'left',
  headerName: '',
};

export function MonitoringAssortmentTable({
  items,
  totalItems,
  pagination,
  includeVat,
  competitors,
  openAddItemsDialog,
  setItemToView,
  loading,
}: Props) {
  const t = useProductsTFunction();
  const { getRowColorForStripedTable } = useRowColorGrouping();

  const columns: GridColDef[] = useMemo(() => {
    const productNameColDef: GridColDef<MonitoringAssortmentItem> = {
      ...baseColumnProps,
      field: 'name',
      headerName: t('products.mgItem.supplierItem.primaryText'),
      minWidth: 180,
      cellClassName: () => dataGridBoldClass,
      renderCell: ({ row }) => {
        const primarySupplierItem = row.getPrimaryOrLowestSalesPriceSupplierItem();
        return (
          <Link
            p={0}
            color="text.primary"
            sx={{ cursor: 'pointer' }}
            onClick={() => setItemToView(Number(row.mgItemNumber))}
          >
            {primarySupplierItem?.primaryText || '-'}
          </Link>
        );
      },
    };
    const mgItemNumberColDef: GridColDef<MonitoringAssortmentItem> = {
      ...baseColumnProps,
      field: 'mgItemNumber',
      minWidth: 130,
      headerName: t('products.mgItem.number.label'),
      renderCell: ({ row }) => (
        <StackedTextCell
          topLine={
            <CopyToClipboard value={row.mgItemNumber} hasIconButtonPadding={false}>
              {row.mgItemNumber}
            </CopyToClipboard>
          }
          slotProps={{ topLine: { sx: { color: 'inherit' } } }}
          nullableBottomLine
        />
      ),
    };

    const salesPriceColDef: GridColDef<MonitoringAssortmentItem> = {
      ...baseColumnProps,
      field: 'salesPrice',
      minWidth: 140,
      renderHeader: () => (
        <ColumnHeaderWithHelpText
          header={t('products.mgItem.supplierItem.salesPrice.title')}
          tooltipContent={t('products.mgItem.supplierItem.salesPrice.tooltipContent')}
        />
      ),
      renderCell: ({ row }) => {
        const salesPrice = row
          .getPrimaryOrLowestSalesPriceSupplierItem()
          ?.currentSalesPrices?.getBracketPackagePrice('F-PAK');

        if (!salesPrice) return <NoPrice />;
        const vatPrice = salesPrice.bracketMarkupPrice.getPriceForClassCode('F-PAK')?.price;
        return (
          <OurPriceCell
            price={includeVat ? vatPrice?.priceWithVat : vatPrice?.priceWithoutVat}
            unit={salesPrice.bracketMarkupPrice.calculationPackage.unit.unit}
            priceDate={salesPrice.validFrom + ''}
          />
        );
      },
    };
    const competitorColDefs: GridColDef<MonitoringAssortmentItem>[] = competitors.map(
      (competitor) => ({
        ...baseColumnProps,
        field: competitor.key(),
        minWidth: 190,
        renderHeader: () => (
          <RetailerColumnHeader
            retailerName={competitor.retailer().name}
            storeName={competitor.name}
            retailerUrlName={competitor.urlName()}
            logoUrl={competitor.circularLogoUrl()}
          />
        ),
        align: 'center',
        headerAlign: 'center',
        sortable: false,
        renderCell: ({ row }) => {
          const currentCompetitorPrice = row.getCurrentCompetitorPrice(competitor.id);
          if (!currentCompetitorPrice) return <NoPrice />;

          const defaultPrice = currentCompetitorPrice?.getPrimaryUnitPrice();
          const previousCompPrice = row.getPreviousCompetitorPrice(competitor.id);
          const previousDefaultPrice = previousCompPrice?.getPrimaryUnitPrice();

          return (
            <CompetitorPriceCell
              currentPrice={{
                price: defaultPrice?.price || 0,
                unit: defaultPrice?.unit || '',
                priceDate: currentCompetitorPrice.lastObservedAt,
              }}
              prevPrice={
                previousDefaultPrice && {
                  price: previousDefaultPrice.price || 0,
                  unit: previousDefaultPrice.unit || '',
                  priceDate: previousCompPrice!.lastObservedAt,
                }
              }
              eqMgItemNumberSrc={currentCompetitorPrice.eqMgItemNumber}
              url={currentCompetitorPrice.productUrl}
              setItemToView={setItemToView}
              includeVat={includeVat}
            />
          );
        },
      })
    );

    return [productNameColDef, mgItemNumberColDef, salesPriceColDef, ...competitorColDefs];
  }, [t, competitors, setItemToView, includeVat]);

  const competitorColumnOrder = useMemo(() => {
    const state = localStorage.getItem('columnOrder');
    return state ? JSON.parse(state) : undefined;
  }, []);

  const saveReorderingOfColumns = useCallback((columnOrder: string[]) => {
    localStorage.setItem('columnOrder', JSON.stringify(columnOrder));
  }, []);

  const sortedColumns = useMemo(() => {
    return competitorColumnOrder
      ? columns.sort(
          (a, b) => competitorColumnOrder.indexOf(a.field) - competitorColumnOrder.indexOf(b.field)
        )
      : columns;
  }, [columns, competitorColumnOrder]);

  return (
    <PaginatedDataGrid
      rowHeight={60}
      columns={sortedColumns}
      getRowClassName={getRowColorForStripedTable}
      onStateChange={(event) => saveReorderingOfColumns(event.columns.orderedFields)}
      rows={items}
      disableRowSelectionOnClick
      localeText={{ noRowsLabel: t('products.productList.noProducts') }}
      disableColumnMenu
      loading={loading}
      pageSizeOptions={[250, 500, 2000, 5000]}
      rowCount={totalItems}
      gridPagination={pagination}
      disableColumnReorder={false}
      slots={{
        noRowsOverlay: () => (
          <NoTableRowsOverlay
            noRowsText={t('products.mgItem.noItems')}
            addAction={
              <AddButton onClick={openAddItemsDialog}>{t('products.mgItem.add')}</AddButton>
            }
          />
        ),
      }}
      initialState={{
        pinnedColumns: { left: ['name', 'mgItemNumber', 'salesPrice'] },
      }}
      sx={{
        '& .MuiDataGrid-columnHeaders': { marginLeft: 0 },
        '& .MuiDataGrid-row': { paddingLeft: 0 },
        '& .MuiDataGrid-topContainer': { paddingLeft: 0, paddingRight: 0 },
      }}
    />
  );
}
